import "@fontsource/montserrat";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/700.css";
import { createTheme, PaletteMode } from "@mui/material/styles";
import { dashboardUiScale } from "./config/config";

const paletteByMode = {
  dark: {
    primary: "#8A00CC",
    secondary: "#32CD32",
    backgroundDefault: "#10131a",
    backgroundPaper: "#181d26",
    textPrimary: "#FFFFFF",
    textSecondary: "#B0B0B0",
    divider: "rgba(255, 255, 255, 0.12)",
    scrollbarThumb: "rgba(255, 255, 255, 0.18)",
    scrollbarThumbHover: "rgba(255, 255, 255, 0.28)",
    scrollbarThumbActive: "rgba(255, 255, 255, 0.36)",
    scrollbarTrack: "rgba(255, 255, 255, 0.04)",
  },
  light: {
    primary: "#7B1FA2",
    secondary: "#1B8A3B",
    backgroundDefault: "#f5f7fb",
    backgroundPaper: "#ffffff",
    textPrimary: "#171923",
    textSecondary: "#5d6472",
    divider: "rgba(20, 24, 36, 0.12)",
    scrollbarThumb: "rgba(20, 24, 36, 0.22)",
    scrollbarThumbHover: "rgba(20, 24, 36, 0.34)",
    scrollbarThumbActive: "rgba(20, 24, 36, 0.46)",
    scrollbarTrack: "rgba(20, 24, 36, 0.06)",
  },
} as const;

export const createLyproTheme = (mode: PaletteMode) => {
  const colors = paletteByMode[mode];
  const scaleRem = (value: number) => `${value * dashboardUiScale}rem`;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: mode === "dark" ? "#D957FF" : "#9C27B0",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: colors.secondary,
      },
      background: {
        default: colors.backgroundDefault,
        paper: colors.backgroundPaper,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
      divider: colors.divider,
    },
    typography: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: 11.9,
      h1: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(6) },
      h2: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(3.75) },
      h3: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(3) },
      h4: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(2.125) },
      h5: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(1.5) },
      h6: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(1.25) },
      subtitle1: { fontSize: scaleRem(1) },
      subtitle2: { fontSize: scaleRem(0.875) },
      body1: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(1) },
      body2: { fontSize: scaleRem(0.875) },
      button: { fontFamily: "'Montserrat', sans-serif", fontSize: scaleRem(0.875) },
      caption: { fontSize: scaleRem(0.75) },
      overline: { fontSize: scaleRem(0.75) },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            colorScheme: mode,
          },
          html: {
            scrollbarWidth: "thin",
            scrollbarColor: `${colors.scrollbarThumb} ${colors.scrollbarTrack}`,
          },
          body: {
            backgroundColor: colors.backgroundDefault,
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${colors.scrollbarThumb} ${colors.scrollbarTrack}`,
          },
          "*::-webkit-scrollbar": {
            width: "10px",
            height: "10px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: colors.scrollbarTrack,
            borderRadius: "999px",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: colors.scrollbarThumb,
            borderRadius: "999px",
            border: `2px solid ${colors.scrollbarTrack}`,
            backgroundClip: "padding-box",
            minHeight: "40px",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: colors.scrollbarThumbHover,
          },
          "*::-webkit-scrollbar-thumb:active": {
            backgroundColor: colors.scrollbarThumbActive,
          },
          "*::-webkit-scrollbar-corner": {
            backgroundColor: "transparent",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            backgroundImage: "none",
            border: `1px solid ${colors.divider}`,
            boxShadow:
              mode === "dark"
                ? "0 24px 70px rgba(0, 0, 0, 0.46)"
                : "0 24px 70px rgba(20, 24, 36, 0.22)",
            overflow: "hidden",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "20px 24px",
            borderBottom: `1px solid ${colors.divider}`,
            fontSize: scaleRem(1.125),
            fontWeight: 500,
            lineHeight: 1.35,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.875),
            minHeight: 30,
          },
          sizeSmall: {
            fontSize: scaleRem(0.8125),
            minHeight: 27,
          },
          outlinedPrimary: {
            color: mode === "dark" ? "#D957FF" : colors.primary,
            borderColor:
              mode === "dark" ? "rgba(217, 87, 255, 0.72)" : colors.primary,
            backgroundColor:
              mode === "dark" ? "rgba(217, 87, 255, 0.08)" : "transparent",
            "&:hover": {
              borderColor:
                mode === "dark" ? "#E78AFF" : colors.primary,
              backgroundColor:
                mode === "dark"
                  ? "rgba(217, 87, 255, 0.16)"
                  : "rgba(123, 31, 162, 0.06)",
            },
            "&.Mui-disabled": {
              color:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.34)"
                  : "rgba(20, 24, 36, 0.32)",
              borderColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.18)"
                  : "rgba(20, 24, 36, 0.18)",
            },
          },
          textPrimary: {
            color: mode === "dark" ? "#D957FF" : colors.primary,
            "&:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(217, 87, 255, 0.12)"
                  : "rgba(123, 31, 162, 0.06)",
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.875),
          },
          sizeSmall: {
            fontSize: scaleRem(0.8125),
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.875),
          },
          sizeSmall: {
            fontSize: scaleRem(0.8125),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.875),
            padding: "12px 12px",
          },
          sizeSmall: {
            fontSize: scaleRem(0.8125),
            padding: "4px 12px",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.875),
            minHeight: 41,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.8125),
          },
          sizeSmall: {
            height: 20,
            fontSize: scaleRem(0.75),
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: scaleRem(0.875),
            minHeight: 30,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "18px 24px",
            backgroundColor: colors.backgroundPaper,
          },
          dividers: {
            borderTop: 0,
            borderBottom: 0,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "10px 8px 10px 24px",
            borderTop: `1px solid ${colors.divider}`,
            backgroundColor: colors.backgroundPaper,
            gap: 8,
            "& .MuiButton-textPrimary, & .MuiButton-textInherit": {
              color: colors.textPrimary,
            },
            "& .MuiButton-textPrimary:hover, & .MuiButton-textInherit:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(20, 24, 36, 0.06)",
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 1,
            alignItems: "center",
            fontFamily: "'Montserrat', sans-serif",
          },
          message: {
            lineHeight: 1.45,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            whiteSpace: "nowrap",
            "&.Mui-selected": {
              color: "#FFFFFF",
              backgroundColor: colors.primary,
            },
            "&.Mui-selected:hover": {
              backgroundColor: mode === "dark" ? "#A700F5" : "#6F1B91",
            },
          },
        },
      },
    },
  });
};

const darkFluorTheme = createLyproTheme("dark");

export default darkFluorTheme;
